import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { OpportunitySet } from "../../../types/opportunities.types"

export interface OpportunitySetSelectorProps {
  sets: OpportunitySet[]
  selectedSet?: OpportunitySet | null
  onSelect: (set: OpportunitySet) => void
  onSeeAll?: () => void
  onCreateSet?: () => void
  canCreateSet?: boolean
  generatingSetIds?: Set<string>
}

export function OpportunitySetSelector({ sets, selectedSet, onSelect, onSeeAll, onCreateSet, canCreateSet = true, generatingSetIds }: OpportunitySetSelectorProps) {
  const { t } = useTranslation()

  const selectableSets = sets.filter((set) => !generatingSetIds?.has(set.id))
  const hasSetOptions = selectableSets.length > 0
  const hasActions = Boolean(onSeeAll || onCreateSet)

  if (sets.length === 0 || (!hasSetOptions && !hasActions)) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" iconStart="folder" iconEnd="chevron-down" className="max-w-xs cursor-pointer">
          <span className="truncate">{selectedSet ? selectedSet.name : t("opportunities.selectSet")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {selectableSets.map((set) => {
          const date = new Date(set.createdAt).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
          return (
            <DropdownMenuItem key={set.id} onClick={() => onSelect(set)} className="flex cursor-pointer flex-col items-start gap-0.5">
              <div className="flex w-full min-w-0 items-center gap-2">
                <Icon name="folder" size="sm" color="muted" className="shrink-0" />
                <span className="min-w-0 truncate font-medium">{set.name}</span>
              </div>
              <span className="pl-6 text-xs text-muted-foreground">{date}</span>
            </DropdownMenuItem>
          )
        })}
        {(onCreateSet || onSeeAll) && <DropdownMenuSeparator />}
        {onCreateSet && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <DropdownMenuItem
                    onClick={canCreateSet ? onCreateSet : undefined}
                    disabled={!canCreateSet}
                    className={canCreateSet ? "cursor-pointer" : ""}
                  >
                    <Icon name="plus" size="sm" color="muted" />
                    <span>{t("opportunities.newSet")}</span>
                  </DropdownMenuItem>
                </div>
              </TooltipTrigger>
              {!canCreateSet && (
                <TooltipContent side="right">
                  <p>{t("common.noPermission")}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
        {onSeeAll && (
          <DropdownMenuItem onClick={onSeeAll} className="cursor-pointer">
            <Icon name="settings" size="sm" color="muted" />
            <span>{t("opportunities.seeAllSets")}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
